import {Deserialize, deserialize, deserializeAs, inheritSerialization} from 'cerialize';
import {Entity} from './entity';
import {ContactCMI} from './contactCMI';
import {InformationsLogement} from './informationsLogement';
import {Adresse} from './adresse';
import {CorrespondantClient} from './correspondantClient';
import {Societe} from './societe';
import {Equipement} from './equipement';
import {Document} from './document';
import {Construction} from './construction';
import {Copropriete} from './copropriete';
import {Dpe} from './dpe';
import {Nuisance} from './nuisance';
import {Zone} from './zone';
import {UserPermissions} from "./userPermissions";
import {ParametrageBatiment} from './rt2012/parametrageBatiment';
import {UsageOccupant} from '../pages/rt2012/parametrage-usages/models/UsageOccupant';
import {ParametrageLogement} from "./rt2012/parametrageLogement";
import {Batiment} from './batiment';
import {User} from "./user";
import {TypeChauffageEnum} from "./rt2012/typeChauffageEnum";
import {TypeECSEnum} from "./rt2012/typeECSEnum";
import {ActiveConsumptionPeriod} from "./rt2012/activeConsumptionPeriod";

const LOGEMENT_NEUF = 'NEUF';
const APPARTEMENT = 'APPARTEMENT';

@inheritSerialization(Entity)
export class Logement extends Entity {


  @deserialize
  nom: string;

  @deserializeAs("numero_fiscal")
  numeroFiscal: number;

  @deserializeAs(Adresse)
  adresse: Adresse;

  @deserializeAs('age_logement')
  ageLogement: string;

  @deserializeAs('contactCMI')
  contactCMI: ContactCMI;

  @deserializeAs('informations_logement')
  informationsLogement: InformationsLogement;

  @deserializeAs(CorrespondantClient, 'correspondant_client')
  correspondantClient;

  @deserialize
  statut: string;

  @deserializeAs(Date, 'date_previsionnelle_livraison')
  datePrevisionnelleLivraison: Date;

  @deserializeAs('numero_certification_nf')
  numeroCertificationNF: string;

  @deserialize
  photo;

  @deserializeAs('taux_completude')
  tauxCompletude: number;

  @deserializeAs('taux_completude_caracteristique')
  tauxCompletudeCaracteristique: number;

  @deserializeAs('taux_completude_document')
  tauxCompletudeDocument: number;

  @deserializeAs(Societe, 'societe')
  societe;

  @deserializeAs('type_logement')
  typeLogement;

  @deserialize
  certifie;

  @deserializeAs('certification_nf')
  certificationNF;

  @deserializeAs('type_certification')
  typeCertification;

  @deserializeAs('nom_maitre_oeuvre')
  nomMaitreOeuvre;

  @deserializeAs('appartenance_terrain_lotissement')
  appartenanceTerrainLotissement;

  @deserializeAs('point_livraison_enedis')
  pointLivraisonEnedis;

  @deserializeAs('point_comptage')
  pointComptage;

  @deserializeAs('parcelle_cadastrale')
  parcelleCadastrale;

  @deserializeAs(Equipement, 'equipements')
  equipements: Array<Equipement>;

  @deserializeAs(Construction, 'construction')
  construction;

  @deserializeAs(Copropriete, 'copropriete')
  copropriete;

  @deserializeAs(Dpe, 'dpe')
  dpe;

  @deserializeAs(Nuisance, 'nuisance')
  nuisance;

  @deserializeAs(Zone, 'zone')
  zone;

  @deserializeAs(Document, 'documents_conseilles')
  documentsConseilles: Document[] = [];

  documentsObligatoires: Document[] = [];

  @deserializeAs(UserPermissions, 'user_permissions')
  userPermissions: UserPermissions;

  @deserializeAs('has_residence')
  hasResidence = false;

  @deserializeAs('rt2012_enabled')
  rt2012Enabled: boolean;

  @deserializeAs(ParametrageBatiment, 'parametrage_batiment_rt2012')
  parametrageBatiment: ParametrageBatiment = null;

  @deserializeAs(ParametrageLogement, 'parametrage_rt2012')
  parametrageLogement: ParametrageLogement = null;

  @deserializeAs(UsageOccupant, 'usage_occupant')
  usageOccupant: UsageOccupant = null;

  @deserializeAs(String, 'parametrage_rt2012_is_complet')
  parametrageRt2012IsComplet: boolean;

  @deserializeAs(String, 'parametrage_rt2012_incomplet_fields')
  parametrageRt2012IncompletFields: Array<string>;

  @deserializeAs(Batiment)
  batiment: Batiment;

  @deserializeAs('current_user_is_occupant')
  currentUserIsOccupant: boolean;

  @deserializeAs('current_user_is_proprietaire')
  currentUserIsProprietaire: boolean;

  @deserializeAs(User, 'locataires')
  locataires: Array<User>;

  @deserializeAs(User, 'createur')
  createur: User;

  @deserializeAs('type_logement_personnalisee')
  typeLogementPersonnalisee;

  @deserializeAs(ActiveConsumptionPeriod,'cooling_active_consumption_periods')
  coolingActiveConsumptionPeriods: Array<ActiveConsumptionPeriod>;

  @deserializeAs(ActiveConsumptionPeriod, 'heating_active_consumption_periods')
  heatingActiveConsumptionPeriods: Array<ActiveConsumptionPeriod>;

  @deserialize
  metadata = [];

  get logementNeuf() {
    return this.ageLogement == LOGEMENT_NEUF;
  }

  get appartement() {
    return this.typeLogement == APPARTEMENT;
  }

  public static OnDeserialized(instance: Logement, json: any): void {
    this.deserializeDocuments(instance, json.documents_obligatoires, 'documentsObligatoires');
  }

  public isOpen(): boolean {
    return this.statut !== 'FERME';
  }

  private static deserializeDocuments(instance, documents, documentsType) {
    let documentsDeserialized = [];
    for (const sectionId in documents) {
      const documentsDs = Deserialize(documents[sectionId], Document);
      documentsDeserialized = documentsDeserialized.concat(documentsDs);
    }
    instance[documentsType] = documentsDeserialized;
  }

  public getDateAchevement() {
    return !this.construction.dateAchevement && this.hasResidenceCarnet()
      ? this.batiment.residence.construction.dateAchevement
      : this.construction.dateAchevement;
  }

  public getNumeroPermisConstruire() {
    return !this.construction.numeroPermisConstruire && this.hasResidenceCarnet()
      ? this.batiment.residence.construction.numeroPermisConstruire
      : this.construction.numeroPermisConstruire;
  }

  public getDateDepotPermisConstruire() {
    return !this.construction.dateDepotPermisConstruire && this.hasResidenceCarnet()
      ? this.batiment.residence.construction.dateDepotPermisConstruire
      : this.construction.dateDepotPermisConstruire;
  }

  public getEtiquetteEnergetique() {
    return !this.dpe.etiquetteEnergetique && this.hasResidenceCarnet()
      ? this.batiment.residence.dpe.etiquetteEnergetique
      : this.dpe.etiquetteEnergetique;
  }

  public getEtiquetteEnvironnementale() {
    return !this.dpe.etiquetteEnvironnementale && this.hasResidenceCarnet()
      ? this.batiment.residence.dpe.etiquetteEnvironnementale
      : this.dpe.etiquetteEnvironnementale;
  }

  public getTypeChauffage() {
    if (!this.construction.typeChauffage && (this.hasResidenceCarnet() || this.parametrageBatiment)) {
      if (this.parametrageBatiment && this.parametrageBatiment.typeProductionChauffage) {
        return this.parametrageBatiment.typeProductionChauffage;
      }
    }
    return this.construction.typeChauffage;
  }

  public getNatureChauffage() {
    if (!this.construction.natureChauffage && (this.hasResidenceCarnet() || this.parametrageBatiment)) {
      if (this.parametrageBatiment && this.parametrageBatiment.typeChauffage) {
        switch (this.parametrageBatiment.typeChauffage){
          case TypeChauffageEnum.CHAUDIERE_GAZ:
          case TypeChauffageEnum.POMPE_CHALEUR_GAZ:
          case TypeChauffageEnum.POMPE_CHALEUR_CHAUDIERE_GAZ:
            return "GAZ"
          case TypeChauffageEnum.CHAUDIERE_FIOUL:
            return "FIOUL"
          case TypeChauffageEnum.RESEAU_CHALEUR:
            return "RESEAU_CHALEUR"
          case TypeChauffageEnum.CONVECTEURS:
          case TypeChauffageEnum.POMPE_CHALEUR_ELEC:
            return "ELECTRICITE"
        }
      }
    }
    return this.construction.natureChauffage;
  }

  public getTypeEauChaude() {
    if (!this.construction.typeEauChaude && (this.hasResidenceCarnet() || this.parametrageBatiment)) {
      if (this.parametrageBatiment && this.parametrageBatiment.typeProductionEcs) {
        return this.parametrageBatiment.typeProductionEcs;
      }
    }
    return this.construction.typeEauChaude;
  }

  public getNatureEauChaude() {
    if (!this.construction.natureEauChaude && (this.hasResidenceCarnet() || this.parametrageBatiment)) {
      if (this.parametrageBatiment && this.parametrageBatiment.typeEcs) {
        switch (this.parametrageBatiment.typeEcs){
          case TypeECSEnum.CHAUDIERES_GAZ:
          case TypeECSEnum.CHAUFFE_EAU_GAZ:
          case TypeECSEnum.POMPE_CHALEUR_CHAUDIERE_GAZ:
          case TypeECSEnum.CHAUDIERE_GAZ:
          case TypeECSEnum.CHAUFFE_EAU_THERMO_GAZ:
          case TypeECSEnum.POMPE_CHALEUR_GAZ:
          case TypeECSEnum.BALLON_SOLAIRE_IND_GAZ:
            return "GAZ"
          case TypeECSEnum.CHAUDIERES_FIOUL:
            return "FIOUL"
          case TypeECSEnum.RESEAU_CHALEUR:
            return "RESEAU_CHALEUR"
          case TypeECSEnum.CHAUFFE_EAU_ELEC:
          case TypeECSEnum.BALLON_SOLAIRE_COL:
          case TypeECSEnum.BALLON_EFFET_JOULE:
          case TypeECSEnum.CHAUFFE_EAU_THERMO_ELEC:
          case TypeECSEnum.BALLON_SOLAIRE_IND_ELEC:
            return "ELECTRICITE"
        }
      }
    }
    return this.construction.natureEauChaude;
  }

  public getTypeClimatisation() {
    if (!this.construction.typeClimatisation && (this.hasResidenceCarnet() || this.parametrageBatiment)) {
      if (this.parametrageBatiment && this.parametrageBatiment.typeProductionRefroidissement) {
        return this.parametrageBatiment.typeProductionRefroidissement;
      }
    }
    if (!this.construction.typeClimatisation && this.parametrageBatiment){
      return 'AUCUN';
    }
    return this.construction.typeClimatisation;
  }

  public getInZoneRisqueTechnologique() {
    return !this.zone.inZoneRisqueTechnologique && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.inZoneRisqueTechnologique
      : this.zone.inZoneRisqueTechnologique;
  }

  public getInZoneRisqueNaturelPrevisible() {
    return !this.zone.inZoneRisqueNaturelPrevisible && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.inZoneRisqueNaturelPrevisible
      : this.zone.inZoneRisqueNaturelPrevisible;
  }

  public getZoneSismicite() {
    return !this.zone.zoneSismicite && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.zoneSismicite
      : this.zone.zoneSismicite;
  }

  public getZoneClimatique() {
    return !this.zone.zoneClimatique && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.zoneClimatique
      : this.zone.zoneClimatique;
  }

  public getInZoneInondable() {
    return !this.zone.inZoneInondable && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.inZoneInondable
      : this.zone.inZoneInondable;
  }

  public getInZonePotentielRadon() {
    return !this.zone.inZonePotentielRadon && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.inZonePotentielRadon
      : this.zone.inZonePotentielRadon;
  }

  public getTermites() {
    return !this.nuisance.termites && this.hasResidenceCarnet()
      ? this.batiment.residence.nuisance.termites
      : this.nuisance.termites;
  }

  public getNomMaitreOeuvre() {
    return !this.nomMaitreOeuvre && this.hasResidenceCarnet()
      ? this.batiment.residence.nomMaitreOeuvre
      : this.nomMaitreOeuvre;
  }

  public getParcelleCadastrale() {
    return !this.parcelleCadastrale && this.hasResidenceCarnet()
      ? this.batiment.residence.parcelleCadastrale
      : this.parcelleCadastrale;
  }

  public getAppartenanceTerrainLotissement() {
    return !this.appartenanceTerrainLotissement && this.hasResidenceCarnet()
      ? this.batiment.residence.appartenanceTerrainLotissement
      : this.appartenanceTerrainLotissement;
  }

  public getMateriauxMurs() {
    if (
      (!this.construction?.materiauxMurs ||
        this.construction.materiauxMurs.length === 0) &&
      this.hasResidenceCarnet()
    ) {
      return this.batiment.residence.construction.materiauxMurs;
    }

    return this.construction?.materiauxMurs;
  }

  public getMateriauxToiture() {
    if (
      (!this.construction?.materiauxToiture ||
        this.construction.materiauxToiture.length === 0) &&
      this.hasResidenceCarnet()
    ) {
      return this.batiment.residence.construction.materiauxToiture;
    }

    return this.construction?.materiauxToiture;
  }

  public getNatureDependances() {
    if (
      (!this.construction?.natureDependances ||
        this.construction.natureDependances.length === 0) &&
      this.hasResidenceCarnet()
    ) {
      return this.batiment.residence.construction.natureDependances;
    }

    return this.construction?.natureDependances;
  }

  public getRaccordementsReseaux() {
    if (
      (!this.construction?.raccordementsReseaux ||
        this.construction.raccordementsReseaux.length === 0) &&
      this.hasResidenceCarnet()
    ) {
      return this.batiment.residence.construction.raccordementsReseaux;
    }

    return this.construction?.raccordementsReseaux;
  }

  public getPointLivraisonEnedis() {
    return !this.pointLivraisonEnedis && this.hasResidenceCarnet()
      ? this.batiment.residence.pointLivraisonEnedis
      : this.pointLivraisonEnedis;
  }
  public getnumeroCertificationNF() {
    return !this.numeroCertificationNF && this.hasResidenceCarnet()
      ? this.batiment.residence.numeroCertificationNF
      : this.numeroCertificationNF;
  }
  public getCertifierNF() {
    return !this.certificationNF && this.hasResidenceCarnet()
      ? this.batiment.residence.certificationNF
      : this.certificationNF;
  }

  public getPointComptage() {
    return !this.pointComptage && this.hasResidenceCarnet()
      ? this.batiment.residence.pointComptage
      : this.pointComptage;
  }

  public getInPerimetrePropectionMonumentHistorique() {
    return !this.zone.inPerimetrePropectionMonumentHistorique && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.inPerimetrePropectionMonumentHistorique
      : this.zone.inPerimetrePropectionMonumentHistorique;
  }

  public getMonumentHistorique() {
    return !this.zone.monumentHistorique && this.hasResidenceCarnet()
      ? this.batiment.residence.zone.monumentHistorique
      : this.zone.monumentHistorique;
  }

  public getPlomb() {
    return !this.nuisance.plomb && this.hasResidenceCarnet()
      ? this.batiment.residence.nuisance.plomb
      : this.nuisance.plomb;
  }

  public getAmiante() {
    return !this.nuisance.amiante && this.hasResidenceCarnet()
      ? this.batiment.residence.nuisance.amiante
      : this.nuisance.amiante;
  }

  public getNumeroImmatriculationCopropriete() {
    return !this.copropriete.numeroImmatriculationCopropriete && this.hasResidenceCarnet()
      ? this.batiment.residence.numeroImmatriculationCopropriete
      : this.copropriete.numeroImmatriculationCopropriete;
  }

  public canRT2012Repartition(): boolean {
    return this.isOpen() && this.rt2012Enabled && this.parametrageBatiment !== null;
  }

  public hasResidenceCarnet(): boolean {
    return null != this.batiment?.residence;
  }
}
